import React, { useState } from "react";
import { useSearchParams, useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import InputField from "components/fields/InputField";
import PasswordStrength from "components/fields/PasswordStrength";
import { adminValidateResetToken, adminResetPassword } from "api/adapters/auth/auth";
import { validatePassword } from "utils/validators";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const { token: paramToken } = useParams();
  const token = searchParams.get("token") || paramToken;
  const navigate = useNavigate();

  const [sPassword, setSPassword] = useState("");
  const [sConfirmPassword, setSConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [error, setError] = useState("");

  const { isLoading: validating, isError: tokenInvalid } = useQuery({
    queryKey: ["validateResetToken", token],
    queryFn: () => adminValidateResetToken(token),
    retry: false,
    enabled: !!token,
  });

  const { mutate: resetPassword, isPending } = useMutation({
    mutationFn: (data) => adminResetPassword(token, data),
    onSuccess: () => {
      navigate("/auth/sign-in", { state: { message: "Password reset successfully. Please sign in." } });
    },
    onError: (err) => {
      setError(err.message || "Something went wrong. Please try again.");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const pErr = validatePassword(sPassword);
    if (pErr) { setPasswordError(pErr); return; }
    if (sPassword !== sConfirmPassword) { setConfirmError("Passwords do not match."); return; }
    setError("");
    resetPassword({ sPassword, sConfirmPassword });
  };

  const renderContent = () => {
    if (!token) {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
            <svg className="h-7 w-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-base font-semibold text-red-500">No reset token provided.</p>
          <Link to="/auth/forgot-password" className="text-sm font-medium text-[#FF7A00] hover:underline">
            Request a reset link
          </Link>
        </div>
      );
    }

    if (validating) {
      return (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#FF7A00] border-t-transparent" />
          <p className="text-sm text-gray-500">Validating reset link...</p>
        </div>
      );
    }

    if (tokenInvalid) {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
            <svg className="h-7 w-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
            </svg>
          </div>
          <p className="text-base font-semibold text-red-500">This reset link is invalid or has expired.</p>
          <Link to="/auth/forgot-password" className="text-sm font-medium text-[#FF7A00] hover:underline">
            Request a new reset link
          </Link>
        </div>
      );
    }

    return (
      <form className="flex flex-col" onSubmit={handleSubmit}>
        <InputField
          variant="auth"
          extra="mb-1"
          label="New Password*"
          placeholder="Min. 8 characters"
          id="sPassword"
          type="password"
          value={sPassword}
          state={passwordError ? "error" : undefined}
          onChange={(e) => {
            setSPassword(e.target.value);
            setPasswordError(validatePassword(e.target.value));
            setConfirmError("");
            setError("");
          }}
        />
        {passwordError && (
          <p className="mb-1 ml-1.5 text-xs font-medium text-red-500">{passwordError}</p>
        )}
        <PasswordStrength password={sPassword} />

        <InputField
          variant="auth"
          extra="mb-1"
          label="Confirm Password*"
          placeholder="Re-enter new password"
          id="sConfirmPassword"
          type="password"
          value={sConfirmPassword}
          state={confirmError ? "error" : sConfirmPassword && sConfirmPassword === sPassword ? "success" : undefined}
          onChange={(e) => {
            setSConfirmPassword(e.target.value);
            setConfirmError(e.target.value !== sPassword ? "Passwords do not match." : "");
            setError("");
          }}
        />
        {confirmError && (
          <p className="mb-3 ml-1.5 text-xs font-medium text-red-500">{confirmError}</p>
        )}
        {!confirmError && sConfirmPassword && sConfirmPassword === sPassword && (
          <p className="mb-3 ml-1.5 text-xs font-medium text-green-500">Passwords match.</p>
        )}
        {!confirmError && (!sConfirmPassword || sConfirmPassword !== sPassword) && <div className="mb-3" />}

        {error && <p className="mb-3 text-sm font-medium text-red-500">{error}</p>}

        <button
          type="submit"
          disabled={isPending || !!passwordError || !!confirmError || !sPassword || !sConfirmPassword}
          className="mt-1 flex w-full items-center justify-center rounded-xl bg-[#FF7A00] py-[12px] text-base font-bold text-white transition duration-200 hover:bg-[#e66a00] active:bg-[#cc5e00] disabled:opacity-60 shadow-[0_4px_14px_0_rgba(255,122,0,0.39)]"
        >
          {isPending ? "Saving..." : "Save"}
        </button>

        <div className="mt-5 text-center">
          <Link to="/auth/sign-in" className="text-sm font-medium text-gray-500 hover:text-[#FF7A00]">
            Back to Sign In
          </Link>
        </div>
      </form>
    );
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white px-4">
      <div className="w-full max-w-[420px] rounded-2xl border border-gray-100 bg-white p-8 shadow-xl">
        <div className="mb-8 flex flex-col items-center text-center">
          <h1
            className="text-[52px] font-black leading-none tracking-tight"
            style={{ color: "#FF7A00", fontFamily: "'Impact', 'Arial Black', sans-serif" }}
          >
            TINKA
          </h1>
          <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-gray-400">
            Your trading reputation
          </p>
        </div>
        <div className="mb-6 h-px bg-gray-100" />
        <h4 className="mb-1 text-2xl font-bold text-navy-700">Reset Password</h4>
        <p className="mb-6 text-sm text-gray-500">Enter your new password below.</p>
        {renderContent()}
      </div>
    </div>
  );
}
